/**
 * TicketTimeline — everything that ever happened to this ticket, in order.
 *
 * Status changes, assignments, field edits, travel legs, parts, time entries,
 * notes, photos, the signature, pricing reveals, the cancellation, and any
 * compliance review it raised. Who did it, and when.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Stack, Chip, CircularProgress, Alert, ToggleButton,
  ToggleButtonGroup, Button,
} from '@mui/material';
import {
  SwapHoriz, PersonAdd, Edit, DirectionsCar, Inventory2, AccessTime,
  ChatBubbleOutline, PhotoCamera, Gesture, AttachMoney, Cancel, Gavel,
  AddCircleOutline, Refresh,
} from '@mui/icons-material';
import { ticketOpsApi } from '../../services/api';
import { useWSRefresh } from '../../contexts/WebSocketContext';

const META: Record<string, { icon: any; color: string; label: string }> = {
  created:    { icon: <AddCircleOutline sx={{ fontSize: 15 }} />, color: '#64748b', label: 'Created' },
  status:     { icon: <SwapHoriz sx={{ fontSize: 15 }} />,        color: '#2563eb', label: 'Status' },
  assignment: { icon: <PersonAdd sx={{ fontSize: 15 }} />,        color: '#E30613', label: 'Assignment' },
  edit:       { icon: <Edit sx={{ fontSize: 15 }} />,             color: '#64748b', label: 'Edit' },
  travel:     { icon: <DirectionsCar sx={{ fontSize: 15 }} />,    color: '#0891b2', label: 'Travel' },
  part:       { icon: <Inventory2 sx={{ fontSize: 15 }} />,       color: '#c2410c', label: 'Parts' },
  time:       { icon: <AccessTime sx={{ fontSize: 15 }} />,       color: '#059669', label: 'Time' },
  note:       { icon: <ChatBubbleOutline sx={{ fontSize: 15 }} />, color: '#475569', label: 'Notes' },
  photo:      { icon: <PhotoCamera sx={{ fontSize: 15 }} />,      color: '#0369a1', label: 'Photos' },
  signature:  { icon: <Gesture sx={{ fontSize: 15 }} />,          color: '#15803d', label: 'Signature' },
  pricing:    { icon: <AttachMoney sx={{ fontSize: 15 }} />,      color: '#b45309', label: 'Pricing' },
  cancel:     { icon: <Cancel sx={{ fontSize: 15 }} />,           color: '#dc2626', label: 'Cancelled' },
  compliance: { icon: <Gavel sx={{ fontSize: 15 }} />,            color: '#9333ea', label: 'Compliance' },
};

const FILTERS = ['all', 'status', 'assignment', 'edit', 'travel', 'part', 'time', 'note', 'photo', 'pricing', 'compliance'];

function when(iso: string) {
  const d = new Date(iso + (iso.endsWith('Z') ? '' : 'Z'));
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function TicketTimeline({ ticketId }: { ticketId: number }) {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    if (!ticketId) return;
    ticketOpsApi.timeline(ticketId)
      .then(r => { setEvents(r.data.events || []); setErr(''); })
      .catch(() => setErr('Could not load the timeline.'))
      .finally(() => setLoading(false));
  }, [ticketId]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  // live: the timeline redraws itself as the job moves
  useWSRefresh(['ticket:updated', 'ticket:status', 'ticket:assigned'], load);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;

  const shown = filter === 'all' ? events : events.filter(e => e.kind === filter);
  const present = Array.from(new Set(events.map(e => e.kind)));

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#14548C' }}>
          Timeline — {events.length} event{events.length === 1 ? '' : 's'}
        </Typography>
        <Button size="small" startIcon={<Refresh sx={{ fontSize: 15 }} />} onClick={load}>Refresh</Button>
      </Stack>

      <ToggleButtonGroup size="small" exclusive value={filter}
        onChange={(_, v) => v && setFilter(v)}
        sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5, '& .MuiToggleButton-root': { border: '1px solid #e2e8f0 !important', borderRadius: '14px !important', px: 1.2, py: 0.2, fontSize: 11, textTransform: 'none' } }}>
        {FILTERS.filter(f => f === 'all' || present.includes(f)).map(f => (
          <ToggleButton key={f} value={f}>{f === 'all' ? 'All' : (META[f]?.label || f)}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {shown.length === 0 && <Typography variant="body2" color="text.secondary">Nothing recorded yet.</Typography>}

      <Box sx={{ position: 'relative', pl: { xs: 2, sm: 2.5 } }}>
        <Box sx={{ position: 'absolute', left: { xs: 7, sm: 9 }, top: 6, bottom: 6, width: 2, bgcolor: '#e2e8f0' }} />
        {shown.map((e, i) => {
          const m = META[e.kind] || META.edit;
          return (
            <Box key={i} sx={{ position: 'relative', mb: 2 }}>
              <Box sx={{
                position: 'absolute', left: { xs: -17, sm: -19 }, top: 1,
                width: 20, height: 20, borderRadius: '50%', bgcolor: '#fff',
                border: `2px solid ${m.color}`, color: m.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {m.icon}
              </Box>
              <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{e.title}</Typography>
                {e.kind === 'compliance' && e.meta?.review_status && (
                  <Chip size="small" label={e.meta.review_status}
                        sx={{ height: 17, fontSize: 9, fontWeight: 700,
                              bgcolor: e.meta.review_status === 'flagged' ? '#fee2e2' : '#ede9fe',
                              color: e.meta.review_status === 'flagged' ? '#b91c1c' : '#6d28d9' }} />
                )}
              </Stack>
              {e.detail && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                  {e.detail}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                {when(e.at)}{e.actor ? ` · ${e.actor}` : ''}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
